import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Checkbox, Tag } from "@threadws/jute";
import { useState } from "react";
import type { Persona, Task, UpdateTask } from "../../api/generated";
import { tasksRemoveMutation, tasksUpdateMutation } from "../../api/generated/@tanstack/react-query.gen";
import type { Day } from "../../utils/calendarDay";
import { dueLabel, formatEstimate, repeatLabel } from "../../utils/taskLabels";
import { invalidateTasks } from "../../utils/taskQueries";
import { TaskEditor } from "./TaskEditor";

interface TaskRowProps {
  task: Task;
  personas: Persona[];
  today: Day;
}

/** A task: tick it off, see when it's due, how often it repeats, how long it takes and how important it is, and edit it. */
export function TaskRow({ task, personas, today }: TaskRowProps) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const refresh = () => invalidateTasks(queryClient);
  const update = useMutation({ ...tasksUpdateMutation(), onSettled: refresh });
  const remove = useMutation({ ...tasksRemoveMutation(), onSuccess: refresh });
  const save = (body: UpdateTask) => update.mutate({ path: { id: task.id }, body });

  // Show the tick straight away, before the API answers.
  const pendingDone = update.isPending ? update.variables.body.done : undefined;
  const done = pendingDone ?? task.completedAt !== null;
  const due = task.dueOn && !done ? dueLabel(task.dueOn, today) : null;

  return (
    <li className="flex flex-col gap-2 border-b border-border-subtle py-2 last:border-b-0">
      <div className="flex items-start gap-3">
        <div className={`min-w-0 flex-1 ${done ? "text-text-secondary line-through" : ""}`}>
          <Checkbox label={task.title} checked={done} onChange={(event) => save({ done: event.target.checked })} />
        </div>
        <div className="flex shrink-0 items-center gap-1.5 text-xs text-text-secondary">
          {task.priority === "high" && !done && <Tag tone="accent">High</Tag>}
          {due && <Tag tone={due.tone}>{due.text}</Tag>}
          {task.recurrence && !done && <span title="Repeats">↻ {repeatLabel(task.recurrence)}</span>}
          {task.estimateMinutes !== null && <span className="font-mono">{formatEstimate(task.estimateMinutes)}</span>}
          <Button variant="ghost" size="sm" icon="" aria-expanded={editing} onClick={() => setEditing(!editing)}>
            {editing ? "Done" : "Edit"}
          </Button>
        </div>
      </div>
      {(update.error ?? remove.error) && <p className="text-xs text-danger">{(update.error ?? remove.error)!.message}</p>}
      {editing && (
        <TaskEditor
          task={task}
          personas={personas}
          onSave={save}
          onDelete={() => remove.mutate({ path: { id: task.id } })}
          deleting={remove.isPending}
        />
      )}
    </li>
  );
}
